import { useRef, useState } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import type { LibraryItem } from "@/components/library/library-item"
import { flowAttachPicks } from "@/components/flow/flow-reference-adder"
import {
  FlowSlotAttachButtons,
  FlowSlotCard,
  FlowSlotEmptyHint,
  FlowSlotFrame,
  FlowSlotHeading,
  flowSlotTile,
  flowSlotViewUrl,
} from "@/components/flow/flow-slot-chrome"
import { libraryPickResult } from "@/hooks/use-library-pick-selection"
import type { FlowAttachOutcome, FlowAttachRequest } from "@/lib/flow-attach-input"
import { REFERENCE_VIDEO_FILE_ACCEPT } from "@/lib/reference-file-policy"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { FlowNodeRow } from "@/types/flow"

type Preview = { url: string; title: string }

type FlowVideoSourceSlotProps = {
  label: string
  mediaLabel: string
  emptyHint: string
  uploadLabel: string
  libraryLabel: string
  disconnectLabel: string
  source?: FlowNodeRow
  sourceName: string
  connected: boolean
  canEdit: boolean
  disabled: boolean
  projectId: number
  frameFormat: string
  frameRate?: number
  onDisconnect: () => void
  onAttach: (batch: FlowAttachRequest) => Promise<FlowAttachOutcome>
}

export function flowVideoSourceName(
  source: FlowNodeRow,
  labels: { video: string; upscale: string; depth: string },
): string {
  if (source.kind === "shot") return source.shot_label || source.entity_name || labels.video
  if (source.kind === "video_upscale") return labels.upscale
  if (source.kind === "video_depth") return labels.depth
  return labels.video
}

/** Один видеовход в том же виде, что слот цепочки: файл, библиотека или источник. */
export function FlowVideoSourceSlot({
  label,
  mediaLabel,
  emptyHint,
  uploadLabel,
  libraryLabel,
  disconnectLabel,
  source,
  sourceName,
  connected,
  canEdit,
  disabled,
  projectId,
  frameFormat,
  frameRate,
  onDisconnect,
  onAttach,
}: FlowVideoSourceSlotProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const busyRef = useRef(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [busy, setBusy] = useState<"file" | "library" | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const locked = disabled || busy != null
  const viewUrl = source ? flowSlotViewUrl(source, "video") : null
  const tile = source ? flowSlotTile(source, "video") : null

  const run = async (which: "file" | "library", batch: FlowAttachRequest) => {
    if (busyRef.current || connected) return null
    busyRef.current = true
    setBusy(which)
    try {
      return await onAttach(batch)
    } finally {
      busyRef.current = false
      setBusy(null)
    }
  }

  const finishLibrary = (pickedSource: ShotFrameSource, item: LibraryItem) => {
    const picks = flowAttachPicks([libraryPickResult(item, pickedSource)], "video")
    if (picks.length === 0) return
    void run("library", { picks }).then((outcome) => {
      if (outcome && outcome.failed === 0 && outcome.attached > 0) setLibraryOpen(false)
    })
  }

  return (
    <div className="relative flex flex-col gap-1.5">
      <FlowSlotHeading label={label} media="video" mediaLabel={mediaLabel} />
      <FlowSlotFrame className={connected ? "p-1" : "gap-2 px-3"}>
        {connected ? (
          <FlowSlotCard
            name={sourceName}
            mediaLabel={mediaLabel}
            tile={tile}
            viewUrl={viewUrl}
            canDisconnect={canEdit}
            disconnectLabel={disconnectLabel}
            disconnectDisabled={locked}
            onOpen={() => {
              if (!viewUrl) return
              setPreview({ url: viewUrl, title: sourceName })
            }}
            onDisconnect={onDisconnect}
          />
        ) : (
          <>
            <FlowSlotEmptyHint>{emptyHint}</FlowSlotEmptyHint>
            {canEdit ? (
              <FlowSlotAttachButtons
                disabled={locked}
                uploadLabel={uploadLabel}
                libraryLabel={libraryLabel}
                onUpload={() => fileRef.current?.click()}
                onLibrary={() => setLibraryOpen(true)}
              />
            ) : null}
          </>
        )}
      </FlowSlotFrame>
      <input
        ref={fileRef}
        type="file"
        accept={REFERENCE_VIDEO_FILE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ""
          if (!file) return
          void run("file", { files: [file] })
        }}
      />
      <LibraryPickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        projectId={projectId}
        frameFormat={frameFormat}
        frameRate={frameRate}
        multiple={false}
        selectionLimit={1}
        subtitle={libraryLabel}
        targets={[{ id: "attach", label, accepts: ["video"] }]}
        defaultTargetId="attach"
        busyTargetId={busy === "library" ? "attach" : null}
        onPick={(_targetId, pickedSource, item) => finishLibrary(pickedSource, item)}
        onPickMany={(_targetId, picked) => {
          const picks = flowAttachPicks(picked, "video")
          if (picks.length === 0) return
          void run("library", { picks }).then((outcome) => {
            if (outcome && outcome.failed === 0 && outcome.attached > 0) setLibraryOpen(false)
          })
        }}
      />
      <ImageLightboxDialog
        open={preview != null}
        onOpenChange={(open) => {
          if (!open) setPreview(null)
        }}
        url={null}
        videoUrl={preview?.url}
        downloadUrl={preview?.url}
        title={preview?.title}
      />
    </div>
  )
}
